import {
  faCopy,
  faEdit,
  faEye,
  faFilter,
  faPlus,
  faSearch,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  clonePayrate,
  deleteAssociationPayrateById,
  getAssociationPayrates,
} from '../../data/actions/ratesActions';
import { IState } from '../../data/reducers/state';
import { IAssociation } from '../../types/association/Association';
import { IPayRate } from '../../types/payrates/PayRate';
import { IPerson } from '../../types/persons/person';
import PayrateEditor from './PayrateEditor';

interface PayrateManagerProps {
  payrates: { [id: string]: IPayRate };
  associations: IAssociation[];
  self: IPerson;

  actions: {
    getAssociationPayrates: () => Promise<IPayRate[]>;
    deleteAssociationPayrateById: (id: string) => Promise<void>;
    clonePayrate: (sourceId: string, cloneData: { name: string; associationId?: string }) => Promise<IPayRate>;
  };
}

const PayrateManager: React.FC<PayrateManagerProps> = ({
  payrates,
  associations,
  self: _self,

  actions,
}) => {
  const [loading, setLoading] = useState(true);
  const [editingPayrate, setEditingPayrate] = useState<IPayRate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssociation, setFilterAssociation] = useState<string>('');
  const [showSystemPayrates, setShowSystemPayrates] = useState(true);

  const isSystemAdmin = true;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await actions.getAssociationPayrates();
    } catch (error: any) {
      console.error('Failed to load payrates:', error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPayrate(null);
    setShowEditor(true);
  };

  const handleEdit = (payrate: IPayRate) => {
    setEditingPayrate(payrate);
    setShowEditor(true);
  };

  const handleDelete = async (payrate: IPayRate) => {
    if (!payrate.associationId) {
      console.error('Cannot delete system payrates');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${payrate.name}"? This action cannot be undone.`)) {
      try {
        await actions.deleteAssociationPayrateById(payrate._id);
        console.log('Payrate deleted successfully');
      } catch (error: any) {
        console.error('Failed to delete payrate:', error.message || error);
      }
    }
  };

  const handleClone = async (payrate: IPayRate) => {
    const cloneName = prompt('Enter name for cloned payrate:', `${payrate.name} (Copy)`);
    if (!cloneName) return;

    try {
      await actions.clonePayrate(payrate._id, { name: cloneName });
      console.log('Payrate cloned successfully');
    } catch (error: any) {
      console.error('Failed to clone payrate:', error.message || error);
    }
  };

  const handleEditorSave = (savedPayrate: IPayRate) => {
    setShowEditor(false);
    setEditingPayrate(null);
    // Refresh data
    loadData();
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setEditingPayrate(null);
  };

  const handleEditorDelete = (payrateId: string) => {
    setShowEditor(false);
    setEditingPayrate(null);
    // Data will be updated via Redux
  };

  // Filter payrates based on search and filters, then sort by creation date (most recent first)
  const payratesList = Object.values(payrates).sort((a, b) => {
    // Sort by timestamp, most recent first (higher timestamp = more recent)
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return bTime - aTime;
  });
  const filteredPayrates = payratesList.filter(payrate => {
    // Search filter
    if (searchTerm && !payrate.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Association filter
    if (filterAssociation) {
      if (filterAssociation === 'system' && payrate.associationId) {
        return false;
      }
      if (filterAssociation !== 'system' && payrate.associationId !== filterAssociation) {
        return false;
      }
    }

    // System payrates filter
    if (!showSystemPayrates && !payrate.associationId) {
      return false;
    }

    return true;
  });

  const getAssociationName = (associationId?: string) => {
    if (!associationId) return 'System';
    const association = associations.find(a => a._id === associationId);
    return association?.name || 'Unknown Association';
  };

  const getPayrateType = (payrate: IPayRate) => {
    const types = [];
    if (payrate.referee) types.push('Referee');
    if (payrate.scoretable) types.push('Scorer');
    if (payrate.refereeCoach) types.push('Coach');
    if (payrate.supervisor) types.push('Supervisor');
    if (payrate.admin) types.push('Admin');
    return types.join(', ') || 'None';
  };

  const canEdit = (_payrate: IPayRate) => {
    // Admin check removed; allow editing for all users
    return true;
  };

  const canDelete = (_payrate: IPayRate) => {
    // Admin check removed; allow deleting for all users
    return true;
  };

  const associationOptions = [
    { value: '', label: 'All Associations' },
    { value: 'system', label: 'System Payrates' },
    ...associations.map(assoc => ({
      value: assoc._id,
      label: assoc.name,
    })),
  ];

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Card padding="lg" shadow="sm">
        <Stack gap="md">
          <Group justify="apart">
            <Title order={2}>Payrate Management</Title>
            {isSystemAdmin && (
              <Button
                onClick={handleCreateNew}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create New Payrate
              </Button>
            )}
          </Group>

          <Stack gap="md">
            <Group>
              <TextInput
                placeholder="Search payrates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftSection={<FontAwesomeIcon icon={faSearch} size="sm" />}
                style={{ flexGrow: 1 }}
              />
              <Select
                placeholder="Filter by association"
                value={filterAssociation}
                onChange={(value) => setFilterAssociation(value || '')}
                data={associationOptions}
                leftSection={<FontAwesomeIcon icon={faFilter} size="sm" />}
                clearable
                style={{ minWidth: 200 }}
              />
            </Group>

            {filteredPayrates.length === 0 ? (
              <Alert title="No payrates found">
                {payratesList.length === 0
                  ? "No payrates available. Create your first payrate to get started."
                  : "No payrates match your current filters."
                }
              </Alert>
            ) : (
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Association</th>
                    <th>Types</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayrates.map(payrate => {
                    const isSystemPayrate = !payrate.associationId;
                    return (
                      <tr
                        key={payrate._id}
                      >
                        <td>
                          <Text weight={500}>
                            {payrate.name}
                          </Text>
                        </td>
                        <td>
                          <Badge
                            color={payrate.associationId ? 'blue' : 'gray'}
                            variant="light"
                          >
                            {getAssociationName(payrate.associationId)}
                          </Badge>
                        </td>
                        <td>
                          <Text size="sm" color="dimmed">
                            {getPayrateType(payrate)}
                          </Text>
                        </td>
                        <td>
                          <Text size="sm" color="dimmed">
                            {payrate.timestamp ? new Date(payrate.timestamp).toLocaleDateString() : '-'}
                          </Text>
                        </td>
                        <td style={{ minWidth: 80 }}>
                          <Group gap="xs">
                            <Tooltip label={!isSystemAdmin && isSystemPayrate ? "View Only (Admin Required)" : "View Details"}>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => handleEdit(payrate)}
                              >
                                <FontAwesomeIcon icon={faEye} size="sm" />
                              </ActionIcon>
                            </Tooltip>

                            {isSystemAdmin && canEdit(payrate) && (
                              <Tooltip label="Edit Payrate">
                                <ActionIcon
                                  variant="subtle"
                                  onClick={() => handleEdit(payrate)}
                                >
                                  <FontAwesomeIcon icon={faEdit} size="sm" />
                                </ActionIcon>
                              </Tooltip>
                            )}

                            {isSystemAdmin && (
                              <Tooltip label="Clone Payrate">
                                <ActionIcon
                                  variant="subtle"
                                  onClick={() => handleClone(payrate)}
                                >
                                  <FontAwesomeIcon icon={faCopy} size="sm" />
                                </ActionIcon>
                              </Tooltip>
                            )}

                            {isSystemAdmin && canDelete(payrate) && (
                              <Tooltip label="Delete Payrate">
                                <ActionIcon
                                  variant="subtle"
                                  c="red"
                                  onClick={() => handleDelete(payrate)}
                                >
                                  <FontAwesomeIcon icon={faTrash} size="sm" />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Stack>
        </Stack>
      </Card>

      <Modal
        opened={showEditor}
        onClose={handleEditorCancel}
        title={editingPayrate ? `Edit Payrate: ${editingPayrate.name}` : 'Create New Payrate'}
        size="xl"
        closeOnClickOutside={false}
      >
        <PayrateEditor
          payrate={editingPayrate}
          isAdmin={isSystemAdmin}
          readOnly={!isSystemAdmin}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onDelete={handleEditorDelete}
        />
      </Modal>
    </>
  );
};

const mapStateToProps = (state: IState) => ({
  payrates: state.payrates.map || {},
  associations: state.associations.admin.map(
    (a) => state.associations.map[a],
  ).filter(Boolean),
  self: state.persons.self && state.persons.map[state.persons.self],
});

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      getAssociationPayrates,
      deleteAssociationPayrateById,
      clonePayrate,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PayrateManager);
